import { createEvent } from 'effector';
import { track } from './amplitude';
import {
  MENU_OPENED,
  MENU_CLOSED,
  MENU_SCENE_SELECTED,
  MENU_TAB_SWITCHED,
} from './events';
import {
  menuOpened,
  $hajjJourneyProgress,
  $historicalJourneyProgress,
} from '../state/sceneProgress';
import { $sceneProgressByPoiFilterId } from '../state/visitedSights';
import { currentSceneId } from './trackerContext';
import { isInsideScene } from '../scene/insideScenes/registry';
import { SCENE_POI_ID } from '../scene/sceneNavigationMap';

/**
 * Menu overlay analytics (§ Menu in README).
 *
 * `menuOpened` is the SAME Effector event that also drives the tour
 * prefetch (`sceneProgress.ts`) — we reuse it so opening always tracks.
 * Close is UI-dispatched from `App.tsx`; tab-switch + scene-select are
 * UI-dispatched from `MenuOverlay` (the click site knows the active tab
 * and card kind).
 */

export type MenuTab = 'hajj' | 'historical';

/** UI-dispatched: the menu was dismissed (X), or closed by an action. */
export const menuClosed = createEvent<{
  outcome: 'dismiss' | 'scene_selected' | 'main_map';
}>();

/** UI-dispatched: the user switched to the OTHER tab (not the reset-on-open). */
export const menuTabSwitched = createEvent<{ tab: MenuTab }>();

/**
 * UI-dispatched from `MenuOverlay` at the click site (it knows the active tab
 * + card kind): a menu card navigated (the WHOLE card is clickable).
 * `targetSceneId` may be a main-scene URL-id (`kaaba`), an era id
 * (`kaaba-600ce`) or an inside-scene id (`kaaba-inside`); `tab` + `targetKind`
 * disambiguate e.g. the Hajj-tab Kaaba card vs the "Kaaba today" slide
 * (both target `kaaba`).
 */
export const menuSceneSelected = createEvent<{
  targetSceneId: string;
  tab: MenuTab;
  targetKind: 'main' | 'era' | 'inside';
}>();

let menuOpenedAt: number | null = null;

export const startMenuTracking = () => {
  menuOpened.watch(() => {
    menuOpenedAt = performance.now();
    const sceneId = currentSceneId();
    const hajj = $hajjJourneyProgress.getState();
    const historical = $historicalJourneyProgress.getState();
    track(MENU_OPENED, {
      scene_id: sceneId,
      is_inside_scene: sceneId != null && isInsideScene(sceneId),
      // Per-tab progress snapshots the user sees at open (POI-based; Hajj
      // incl. the inside-scene pseudo-POIs). May still be filling in if the
      // prefetch hasn't resolved yet — this is the immediate cached state.
      explored_pois: hajj.visited,
      total_pois: hajj.total,
      journey_percent: hajj.percent,
      // `null` until a historical tour is cached (and always in Balady,
      // where the tab is hidden).
      historical_percent: historical.hasData ? historical.percent : null,
    });
  });

  menuTabSwitched.watch(({ tab }) => {
    track(MENU_TAB_SWITCHED, {
      scene_id: currentSceneId(),
      tab,
    });
  });

  menuClosed.watch(({ outcome }) => {
    track(MENU_CLOSED, {
      scene_id: currentSceneId(),
      dwell_ms:
        menuOpenedAt != null ? Math.round(performance.now() - menuOpenedAt) : null,
      outcome,
    });
    menuOpenedAt = null;
  });

  menuSceneSelected.watch(({ targetSceneId, tab, targetKind }) => {
    // Progress of the SELECTED scene → discovery (unexplored) vs revisit.
    const progress =
      $sceneProgressByPoiFilterId.getState()[SCENE_POI_ID[targetSceneId] ?? targetSceneId];
    track(MENU_SCENE_SELECTED, {
      target_scene_id: targetSceneId,
      from_scene_id: currentSceneId(),
      tab,
      target_kind: targetKind,
      target_explored: progress?.hasData ? progress.visited > 0 : null,
      target_visited: progress?.visited ?? null,
      target_total: progress?.total ?? null,
    });
  });
};
